import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useSupabase } from './supabase'
import { PushNotifications } from '@capacitor/push-notifications'
import { Capacitor } from '@capacitor/core'

const NOTIFICATIONS_STORAGE_KEY = 'gaze-notifications-enabled'

export const useNotificationsSettings = defineStore('notifications-settings', () => {
  const supabase = useSupabase()
  const isEnabled = ref(true)
  const isInitialized = ref(false)

  // Get notifications preference from localStorage
  function getStoredNotifications(): boolean | null {
    if (typeof window === 'undefined') return null
    const stored = localStorage.getItem(NOTIFICATIONS_STORAGE_KEY)
    if (stored === null) return null
    return stored === 'enabled'
  }

  // Store notifications preference in localStorage
  function storeNotifications(enabled: boolean) {
    if (typeof window === 'undefined') return
    localStorage.setItem(NOTIFICATIONS_STORAGE_KEY, enabled ? 'enabled' : 'disabled')
  }

  // Clear notifications preference from localStorage (for testing)
  function clearStoredNotifications() {
    if (typeof window === 'undefined') return
    localStorage.removeItem(NOTIFICATIONS_STORAGE_KEY)
  }

  // Initialize notifications preference
  function initializeNotifications() {
    if (isInitialized.value) return

    // First, try to get from localStorage
    const storedNotifications = getStoredNotifications()
    if (storedNotifications !== null) {
      isEnabled.value = storedNotifications
    } else if (
      supabase.user.status === 'SignedIn' &&
      supabase.user.data.notifications_enabled !== undefined
    ) {
      isEnabled.value = supabase.user.data.notifications_enabled
      storeNotifications(isEnabled.value)
    } else {
      // Default to enabled
      isEnabled.value = true
      storeNotifications(isEnabled.value)
    }

    isInitialized.value = true
  }

  // Request notification permissions
  async function requestPermissions(): Promise<boolean> {
    if (Capacitor.getPlatform() === 'web') {
      // For web, use the browser's notification API
      if ('Notification' in window) {
        const permission = await Notification.requestPermission()
        return permission === 'granted'
      }
      return false
    } else {
      // For mobile, use Capacitor's PushNotifications
      try {
        let permStatus = await PushNotifications.checkPermissions()

        if (permStatus.receive === 'prompt') {
          permStatus = await PushNotifications.requestPermissions()
        }

        return permStatus.receive === 'granted'
      } catch (error) {
        console.error('Error requesting notification permissions:', error)
        return false
      }
    }
  }

  // Register for push notifications
  async function registerForNotifications(): Promise<boolean> {
    if (Capacitor.getPlatform() === 'web') {
      // Web notifications are handled differently
      return true
    } else {
      try {
        await PushNotifications.register()
        return true
      } catch (error) {
        console.error('Error registering for notifications:', error)
        return false
      }
    }
  }

  // Enable notifications
  async function enableNotifications(): Promise<boolean> {
    try {
      // Request permissions first
      const permissionGranted = await requestPermissions()

      if (!permissionGranted) {
        // If permissions are denied, keep notifications disabled
        isEnabled.value = false
        storeNotifications(false)

        // Update database
        if (supabase.user.status === 'SignedIn') {
          await supabase.api
            .from('user_settings')
            .update({ notifications_enabled: false })
            .eq('user_id', supabase.user.data.id)

          await supabase.fetchProfile(supabase.user.data.id)
        }

        return false
      }

      // Register for notifications
      const registrationSuccess = await registerForNotifications()

      if (!registrationSuccess) {
        isEnabled.value = false
        storeNotifications(false)

        // Update database
        if (supabase.user.status === 'SignedIn') {
          await supabase.api
            .from('user_settings')
            .update({ notifications_enabled: false })
            .eq('user_id', supabase.user.data.id)

          await supabase.fetchProfile(supabase.user.data.id)
        }

        return false
      }

      // Update state
      isEnabled.value = true
      storeNotifications(true)

      // Update database
      if (supabase.user.status === 'SignedIn') {
        await supabase.api
          .from('user_settings')
          .update({ notifications_enabled: true })
          .eq('user_id', supabase.user.data.id)

        await supabase.fetchProfile(supabase.user.data.id)
      }

      return true
    } catch (error) {
      console.error('Error enabling notifications:', error)
      return false
    }
  }

  // Disable notifications
  async function disableNotifications(): Promise<boolean> {
    try {
      // Update state
      isEnabled.value = false
      storeNotifications(false)

      // Update database
      if (supabase.user.status === 'SignedIn') {
        await supabase.api
          .from('user_settings')
          .update({ notifications_enabled: false })
          .eq('user_id', supabase.user.data.id)

        await supabase.fetchProfile(supabase.user.data.id)
      }

      return true
    } catch (error) {
      console.error('Error disabling notifications:', error)
      return false
    }
  }

  // Toggle notifications
  async function toggleNotifications(): Promise<boolean> {
    if (isEnabled.value) {
      return await disableNotifications()
    } else {
      return await enableNotifications()
    }
  }

  // Sync with database
  async function syncWithDatabase() {
    if (
      supabase.user.status === 'SignedIn' &&
      supabase.user.data.notifications_enabled !== undefined
    ) {
      if (isEnabled.value !== supabase.user.data.notifications_enabled) {
        isEnabled.value = supabase.user.data.notifications_enabled
        storeNotifications(isEnabled.value)
      }
    }
  }

  return {
    isEnabled: computed(() => isEnabled.value),
    isInitialized: computed(() => isInitialized.value),
    initializeNotifications,
    enableNotifications,
    disableNotifications,
    toggleNotifications,
    requestPermissions,
    syncWithDatabase,
    getStoredNotifications,
    storeNotifications,
    clearStoredNotifications
  }
})
